// exp_PORTFOLIO-58018_file
import { ChangeEventHandler, FC, useRef } from 'react';

import { REQUIREMENTS_MAX_LENGTH } from 'utils/vrCampaign';

import vrPackageFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vr_package/vr_package_form_field_touch';
import { Flex, Text, TextArea, VSpacing } from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { TooltipCommon } from 'components/common/TooltipCommon/TooltipCommon';

import {
    selectVrCampaignErrors,
    selectVrCampaignRequirements,
    setVrCampaignErrors,
    setVrCampaignRequirements,
} from 'store/models/vrCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'VrCampaignBuilderPage.requirements.title',
    description: 'VrCampaignBuilderPage.requirements.description',
    tooltip: 'VrCampaignBuilderPage.requirements.tooltip',
    placeholder: 'VrCampaignBuilderPage.requirements.placeholder',
    error: 'VrCampaignBuilderPage.requirements.error',
};

export const Requirements: FC = () => {
    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);

    const requirements = useSelector(selectVrCampaignRequirements);
    const hasError = useSelector(selectVrCampaignErrors).requirements;

    const trls = useTrls();
    const tooltipRef = useRef(null);
    const dispatch = useDispatch();

    const validateRequirements = (value: string) => {
        dispatch(setVrCampaignErrors({ requirements: value.length > REQUIREMENTS_MAX_LENGTH }));
    };

    const handleRequirementsChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
        const { value } = event.target;
        dispatch(setVrCampaignRequirements(value));
        validateRequirements(value);
    };

    const handleRequirementsBlur = () => {
        validateRequirements(requirements);
        vrPackageFormFieldTouch({ fieldName: 'hidden_requirements', length: requirements.length });
    };

    return (
        <div>
            <Flex direction="row" gap={6} align="center">
                <Text>{trls(TrlKeys.title)}</Text>
                <QuestionCircleOutlinedSize16
                    initialColor="secondary"
                    ref={tooltipRef}
                    onClick={setIsTooltipVisibleOn}
                    data-qa="vr-campaign-requirements-tooltip"
                />
            </Flex>
            <VSpacing default={4} />
            <Text style="secondary">{trls(TrlKeys.description)}</Text>
            <VSpacing default={12} />
            <TextArea
                placeholder={trls(TrlKeys.placeholder)}
                maxLength={REQUIREMENTS_MAX_LENGTH}
                size="large"
                value={requirements}
                layout="fill-horizontal"
                invalid={hasError}
                errorMessage={trls(TrlKeys.error)}
                onChange={handleRequirementsChange}
                onFocus={() => dispatch(setVrCampaignErrors({ requirements: false }))}
                onBlur={handleRequirementsBlur}
                data-qa="vr-campaign-requirements-input"
            />
            <TooltipCommon
                activatorRef={tooltipRef}
                tooltipContent={trls(TrlKeys.tooltip)}
                onClose={setIsTooltipVisibleOff}
                visible={isTooltipVisible}
                showClose
            />
        </div>
    );
};
